import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Función helper para determinar si el usuario es admin
  const isUserAdmin = userData => {
    if (!userData) return false;
    // Verificar si tiene el rol 'admin' en el array de roles
    if (userData.roles && Array.isArray(userData.roles)) {
      return userData.roles.includes('admin');
    }
    // Verificar propiedades legacy
    return userData.role === 'admin' || userData.isAdmin === true;
  };

  // Función para cargar datos iniciales desde localStorage
  const loadInitialData = () => {
    const currentApp = localStorage.getItem('currentApp') || 'card';

    // Cargar datos de autenticación para obtener servicios del usuario
    const authData = localStorage.getItem('auth');
    let userServices = [];
    let isAdmin = false;

    if (authData) {
      try {
        const userData = JSON.parse(authData);
        userServices = userData.services || [];

        // Verificar si el usuario es admin
        isAdmin = isUserAdmin(userData);
      } catch (error) {
        console.error('Error parsing auth data:', error);
        userServices = [];
        isAdmin = false;
      }
    }

    return { currentApp, userServices, isAdmin };
  };

  const initialData = loadInitialData();
  const [currentApp, setCurrentApp] = useState(initialData.currentApp);
  const [userServices, setUserServices] = useState(initialData.userServices);
  const [isAdmin, setIsAdmin] = useState(initialData.isAdmin);

  // Función para cambiar de aplicación
  const switchApp = useCallback(appType => {
    setCurrentApp(appType);
    localStorage.setItem('currentApp', appType);
    // Disparar evento para que otros componentes se actualicen
    window.dispatchEvent(new Event('appChanged'));
  }, []);

  // Función para actualizar servicios del usuario
  const updateUserServices = useCallback(services => {
    setUserServices(services);
  }, []);

  // Función para actualizar estado de admin
  const updateAdminStatus = useCallback(adminStatus => {
    setIsAdmin(adminStatus);
  }, []);

  // Función para refrescar datos del usuario desde localStorage
  const refreshUserData = useCallback(() => {
    const authData = localStorage.getItem('auth');
    if (authData) {
      try {
        const userData = JSON.parse(authData);
        const services = userData.services || [];
        const adminStatus = isUserAdmin(userData);

        setUserServices(services);
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Error refreshing user data:', error);
        setUserServices([]);
        setIsAdmin(false);
      }
    } else {
      setUserServices([]);
      setIsAdmin(false);
    }
  }, []);

  // Efecto para escuchar cambios en localStorage
  useEffect(() => {
    const handleStorageChange = e => {
      if (e.key === 'currentApp') {
        setCurrentApp(e.newValue || 'card');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Efecto para escuchar cambios de aplicación en la misma pestaña
  useEffect(() => {
    const handleAppChange = () => {
      const newApp = localStorage.getItem('currentApp') || 'card';
      if (newApp !== currentApp) {
        setCurrentApp(newApp);
      }
    };

    window.addEventListener('appChanged', handleAppChange);
    return () => window.removeEventListener('appChanged', handleAppChange);
  }, [currentApp]);

  // Función para determinar si el usuario puede acceder a una aplicación
  const canAccessApp = useCallback(
    appType => {
      if (isAdmin) return true; // Los admins pueden acceder a todo

      const appServiceMap = {
        card: 'scuore-card',
        quality: 'scuore-quality',
      };

      return userServices.includes(appServiceMap[appType]);
    },
    [isAdmin, userServices]
  );

  // Función para obtener las aplicaciones disponibles para el usuario
  const getAvailableApps = useCallback(() => {
    if (isAdmin) return ['card', 'quality'];

    const availableApps = [];
    if (userServices.includes('scuore-card')) availableApps.push('card');
    if (userServices.includes('scuore-quality')) availableApps.push('quality');

    return availableApps;
  }, [isAdmin, userServices]);

  const value = useMemo(
    () => ({
      currentApp,
      userServices,
      isAdmin,
      switchApp,
      updateUserServices,
      updateAdminStatus,
      refreshUserData,
      canAccessApp,
      getAvailableApps,
    }),
    [
      currentApp,
      userServices,
      isAdmin,
      switchApp,
      updateUserServices,
      updateAdminStatus,
      refreshUserData,
      canAccessApp,
      getAvailableApps,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
